import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { SubscriptionType } from "@shaarp/common";
import { Loader2Icon } from "lucide-react";
import { FormEvent, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import { useToast, useToastError } from "../ui/use-toast";

export default function DialogInviteBilling({
  subscriptionType,
  pendingProLicenseCount,
  onClose = () => {},
  onCompleted = () => {},
}: {
  subscriptionType?: SubscriptionType;
  pendingProLicenseCount?: number;
  onClose: () => void;
  onCompleted?: () => void | Promise<void>;
}) {
  const toastError = useToastError();
  const api = useApi();
  const { toast } = useToast();
  const { t } = useTranslation();

  const [email, setEmail] = useState<string>("");

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!email) {
      toastError();
      return;
    }

    try {
      setIsLoading(true);
      await api.post(`/my/team/setup/billing/invite-user`, { email });

      toast({
        variant: "default",
        title: t("team.components.inviteBilling.toast.success.title"),
        description: t(
          "team.components.inviteBilling.toast.success.description",
          { email }
        ),
      });

      onCompleted();
    } catch (_) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[600px] w-full">
        <DialogHeader>
          <DialogTitle>{t("team.components.inviteBilling.title")}</DialogTitle>
        </DialogHeader>
        <DialogDescription
          dangerouslySetInnerHTML={{
            __html: t("team.components.inviteBilling.description"),
          }}
        />
        <form onSubmit={onSubmit}>
          <Separator className="my-4" />
          <div className="overflow-auto max-h-[70vh] px-3">
            <div className="space-y-5">
              <div className="mb-4 font-semibold text-center text-primary">
                {t("team.components.inviteBilling.licenseInfo", {
                  count: pendingProLicenseCount,
                  period:
                    subscriptionType === "annual"
                      ? t("team.components.inviteBilling.period.year")
                      : t("team.components.inviteBilling.period.month"),
                })}
              </div>
              <div className="grid w-full items-center gap-1.5">
                <Label htmlFor="email">
                  {t("team.components.inviteBilling.form.emailLabel")}
                </Label>
                <Input
                  type="email"
                  id="email"
                  placeholder={t(
                    "team.components.inviteBilling.form.emailPlaceholder"
                  )}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>
          <Separator className="my-4" />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost">
                {t("team.components.inviteBilling.buttons.cancel")}
              </Button>
            </DialogClose>
            {isLoading ? (
              <Button disabled>
                <Loader2Icon className="w-4 h-4 animate-spin" />
              </Button>
            ) : (
              <Button type="submit">
                {t("team.components.inviteBilling.buttons.invite")}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
